import type { CSSProperties } from 'react';
import { TmrMark } from './TmrMark';

export interface WordmarkProps {
  /** Pixel size of the "TMR" lettering. */
  size?: number;
  /** Any CSS color. Defaults to deep forest; pass `var(--cream-50)` on dark canvases. */
  color?: string;
}

/**
 * TMR wordmark — "TMR" in the serif that is now the logo's alone.
 * Pure markup, no images; inherits `currentColor`.
 * Shared identity primitive — reused by the brand canvas and app headers.
 *
 * Under the "site" design system (`<html data-ds="site">`) the same element shows the site's logo mark
 * alone instead, with no lettering: both renditions are in the markup and the theme's CSS picks
 * one, so no consumer has to know which theme is on. The mark is `hidden` by default, which keeps the
 * classic rendering exactly as it was for any app that never loads the site theme.
 */
export function Wordmark({ size = 30, color = 'var(--forest-950)' }: WordmarkProps) {
  const wrap: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'baseline',
    fontSize: size,
    lineHeight: 1,
    color,
    userSelect: 'none',
  };

  const serif: CSSProperties = {
    fontFamily: 'var(--font-wordmark)',
    letterSpacing: '-0.035em',
    fontWeight: 400,
  };

  return (
    <span className="tmr-wordmark" style={wrap} role="img" aria-label="TMR">
      <span className="tmr-wordmark__serif" style={serif} aria-hidden="true">
        TMR
      </span>
      <span className="tmr-wordmark__mark" hidden aria-hidden="true">
        <TmrMark title="" />
      </span>
    </span>
  );
}
